import Button from 'components/base/Button'
import FormItem from 'components/base/FormItem'
import Input from 'components/base/Input'
import { FC, c } from 'lib/component-utils'
import { useUserContext } from 'lib/context'
import { useCallback, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { Updater } from 'use-immer'
import { SignUpProcessState, UserFormData } from './lib/types'
import PasswordInput from 'components/base/PasswordInput'
import PhoneNumberInput from 'components/base/PhoneNumberInput'
import { ArrowNarrowRightIcon } from '@heroicons/react/solid'
import { useIntl } from 'lib/intl-utils'
import { isPossiblePhoneNumber } from 'react-phone-number-input'
import Badge from 'components/base/Badge'
import { usePromoCodeContext } from 'components/landing/lib/promoCodeContext'
import { useReCaptcha } from 'next-recaptcha-v3'
import { useTrackGoal } from 'lib/metrics'
import PromoCode from 'components/landing/PromoCode'
import Link from 'components/base/Link'
import { match } from 'ts-pattern'
import { amplitude } from 'components/app/Amplitude'
import Separator from 'components/base/Separator'

type Props = {
  processState: SignUpProcessState
  setProcessState: Updater<SignUpProcessState>
  defaultTrialDays: number | null
  error?: string
  includeHeader?: boolean
  redirect?: string
}

const UserInfoForm: FC<Props> = ({
  processState,
  setProcessState,
  defaultTrialDays,
  error,
  includeHeader = true,
  redirect,
}) => {
  const ctx = useUserContext()
  const promoCode = usePromoCodeContext()
  const { t, lang } = useIntl()
  // const { executeRecaptcha, loaded: recaptchaLoaded } = useReCaptcha()
  const trackGoal = useTrackGoal()
  const [generalError, setGeneralError] = useState(
    error ? t`login.oauth_error` + ' ' + error : null
  )
  const [phoneOrEmail, setPhoneOrEmail] = useState<'phone' | 'email'>('phone')
  const {
    register,
    control,
    handleSubmit,
    formState: { errors, dirtyFields },
    watch,
    reset,
    setValue,
  } = useForm<UserFormData>({ mode: 'onSubmit' })
  const [autoFocusEnabled, setAutoFocusEnabled] = useState(false)

  const phone = watch('phone')
  const password = watch('password')

  const freeDays = promoCode?.freeDays ?? defaultTrialDays ?? 2

  useEffect(() => {
    if (ctx.theme.hostType === 'sellscreen') {
      return
    }
    if (phone == null) {
      setPhoneOrEmail('email')
      return
    }

    if (phone.includes(' ')) {
      setValue('phone', phone.replaceAll(' ', ''))
      return
    }

    if (
      phone.startsWith('+') ||
      phone.startsWith('7') ||
      phone.startsWith('8') ||
      phone.startsWith('9')
    ) {
      setPhoneOrEmail('phone')
      if (phone.startsWith('9')) {
        setValue('phone', '+7' + phone)
      }
      if (phone.startsWith('8')) {
        setValue('phone', '+7' + phone.slice(1))
      }
    }
  }, [phone])

  const onUserDataSubmit = useCallback(
    async (data: UserFormData) => {
      if (ctx.theme.hostType === 'sellmonitor') {
        // if (!recaptchaLoaded) return
        window.scrollTo({ top: 0 })

        // const recaptchaToken = await executeRecaptcha('register')
        const response = await ctx.registerSendAuthCode(data.phone)

        match(response)
          .with({ status: 'AuthCodeSent' }, (response) => {
            setProcessState((s) => {
              s.stage = 'waitingForCode'
              s.data = data
              s.codeType = response.type
              s.phone = data.phone
            })
          })
          .with({ status: 'AttemptsLimitReached' }, (response) => {
            setGeneralError(
              t`login.attempts_limit_reached` +
                ` ${response.seconds} ` +
                t('second_accusative', { count: response.seconds })
            )
          })
          .with({ error: 'FormValidationErrors' } as any, (response) => {
            setGeneralError(t`check_form_for_errors`)
          })
          .otherwise(() => {
            setGeneralError(t`something_went_wrong`)
          })
      }
      if (ctx.theme.hostType === 'sellscreen' || ctx.theme.hostType === 'uzum') {
        ctx
          .registerUser({
            email: data.email,
            fullName: data.firstName + ' ' + data.lastName,
            phone: data.phone,
            plainPassword: data.password,
          })
          .then(() => {
            if (redirect) {
              window.location.href = redirect
            } else {
              window.location.href = '/trial/welcome/'
            }
          })
      }
    },
    [] //executeRecaptcha, recaptchaLoaded]
  )

  useEffect(() => {
    if (!processState.data) return
    reset(processState.data)
  }, [processState.data])

  useEffect(() => {
    setTimeout(() => {
      setAutoFocusEnabled(true)
    }, 100)
  }, [])

  useEffect(() => {
    amplitude.track('Signup Started')
  }, [])

  return (
    <>
      {includeHeader && (
        <div className="mt-20">
          <div className="flex items-center space-x-2">
            <h2 className="text-lg font-semibold text-gray-900">{t`registration.get_started`}</h2>
            {!promoCode && (
              <Badge className="ring-2 ring-lime-300">
                {freeDays} {t('day', { count: freeDays })}
              </Badge>
            )}
            {promoCode && <PromoCode alwaysShortText promoCode={promoCode} />}
          </div>
          <p className="mt-2 text-sm text-gray-700">
            {t`registration.already_registered`}{' '}
            <Link href="/login/" className="font-medium text-primary-600 hover:underline">
              {t`registration.sign_in`}
            </Link>{' '}
            {t`registration.to_your_account`}
          </p>
        </div>
      )}
      <form
        onSubmit={handleSubmit(onUserDataSubmit)}
        className="grid grid-cols-1 gap-x-6 gap-y-6 mt-10 sm:grid-cols-2"
      >
        {ctx.theme.hostType !== 'sellmonitor' && (
          <FormItem label={t`first_name`} error={errors.firstName?.message}>
            <Input
              {...register('firstName', { required: t`required_field` })}
              hasError={Boolean(errors.firstName?.message)}
              type="text"
              autoComplete="given-name"
            />
          </FormItem>
        )}
        {ctx.theme.hostType !== 'sellmonitor' && (
          <FormItem label={t`last_name`} error={errors.lastName?.message}>
            <Input
              {...register('lastName', { required: t`required_field` })}
              hasError={Boolean(errors.lastName?.message)}
              type="text"
              autoComplete="family-name"
            />
          </FormItem>
        )}
        {ctx.theme.hostType !== 'sellmonitor' && (
          <FormItem label="Email" error={errors.email?.message} className="col-span-full">
            <Input
              {...register('email', {
                required: t`required_field`,
                validate: {
                  isValid: (v) =>
                    /^[^\.@\s](?:[^@\s]*[^\.@\s])?@[^\s@]+\.[^\s@]+$/.test(v) ||
                    t`registration.invalid_email`,
                  isAvailable: async (v) => {
                    const testResult = await ctx.testEmail(v)
                    if (testResult === 'available') {
                      return true
                    }
                    return t`registration.email_taken`
                  },
                },
              })}
              hasError={Boolean(errors.email?.message)}
              type="email"
              autoComplete="email"
            />
          </FormItem>
        )}
        <FormItem label={t`phone`} error={errors.phone?.message} className="col-span-full">
          {phoneOrEmail === 'email' ? (
            <Input
              autoFocus={autoFocusEnabled}
              onInput={(e) => {
                e.currentTarget.value = e.currentTarget.value.replace(/[^0-9+]/g, '')
              }}
              {...register('phone', {
                required: t`required_field`,
              })}
              hasError={Boolean(errors.phone?.message)}
              type="text"
              autoComplete="phone"
            />
          ) : (
            <PhoneNumberInput
              autoFocus={autoFocusEnabled && ctx.theme.hostType !== 'sellscreen'}
              onKeyDown={(e) => {
                const key = e.key
                if (phone == null) return

                if (key === 'Backspace' && (!phone || phone.length === 0 || phone.length === 1)) {
                  e.preventDefault()
                  setPhoneOrEmail('email')
                }
              }}
              name="phone"
              rules={{
                required: t`required_field`,
                validate: {
                  isPossible: (v: string) =>
                    isPossiblePhoneNumber(v) || t`registration.invalid_phone`,
                  isAvailable: async (v: string) => {
                    const testResult = await ctx.testPhone(v)
                    if (testResult === 'available') {
                      return true
                    }
                    return t`registration.phone_taken`
                  },
                },
              }}
              control={control as any}
              hasError={Boolean(errors.phone?.message)}
            />
          )}
        </FormItem>
        {ctx.theme.hostType !== 'sellmonitor' && (
          <FormItem label={t`password`} error={errors.password?.message} className="">
            <PasswordInput
              {...register('password', {
                required: t`required_field`,
                minLength: {
                  value: 8,
                  message: t`password_less_than_8`,
                },
                maxLength: {
                  value: 191,
                  message: t`password_more_than_191`,
                },
                validate: {
                  hasNumber: (v) => /\d/.test(v) || t`password_are_not_match_numbers`,
                  hasLetter: (v) => /[a-z]/i.test(v) || t`password_are_not_match_latin`,
                },
              })}
              hasError={Boolean(errors.password?.message)}
              autoComplete="new-password"
            />
          </FormItem>
        )}
        {ctx.theme.hostType !== 'sellmonitor' && (
          <FormItem label={t`confirm_password`} error={errors.repeatPassword?.message} className="">
            <PasswordInput
              {...register('repeatPassword', {
                required: t`required_field`,
                validate: {
                  matchesPassword: (v) => v === password || t`passwords_dont_match`,
                },
              })}
              hasError={Boolean(errors.repeatPassword?.message)}
              autoComplete="new-password"
            />
          </FormItem>
        )}
        <div className="col-span-full space-y-2">
          {generalError && <p className="text-red-600">{generalError}</p>}
          <Button
            disabled={false} //!recaptchaLoaded}
            type="submit"
            theme="primary"
            className="justify-center mt-0 space-x-1 w-full"
          >
            <span>{t`sign_up`} </span>
            <ArrowNarrowRightIcon className="w-4 h-4" />
          </Button>
          {ctx.theme.hostType === 'sellmonitor' && (
            <div className="relative">
              <Separator orientation="horizontal" className="my-6" />
              <div className="absolute left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2 bg-background px-2.5 bg-white text-xs text-gray-500">
                {t`or`}
              </div>
            </div>
          )}
          {ctx.theme.hostType === 'sellmonitor' && (
            <div className="flex flex-col">
              <a
                href="/oauth/start/vk/"
                className={c`focus:ring-primary-500 font-medium text-sm rounded-md h-[38px] flex items-center justify-center gap-[7px] hover:bg-[#F5F5F7] bg-white border border-black/[0.12]`}
              >
                <img src={`/next/img/oauth/vk_logo_blue.svg`} className="size-6" />
                <span>{t`login.sign_in_with_vk_id`}</span>
              </a>
            </div>
          )}
          <p className="text-xs text-center text-gray-500 !mt-8">
            {ctx.theme.hostType === 'sellmonitor' && (
              <>
                Регистрируясь, вы соглашаетесь с{' '}
                <a
                  target="_blank"
                  rel="noreferrer"
                  href="/public-offer/"
                  className="text-primary-600"
                >
                  публичной офертой
                </a>{' '}
                и{' '}
                <a
                  target="_blank"
                  rel="noreferrer"
                  href="/doc/sellmonitor/ru/privacy_policy.pdf"
                  className="text-primary-600"
                >
                  политикой конфиденциальности
                </a>
              </>
            )}
            {ctx.theme.hostType === 'sellscreen' && (
              <>
                By signing up, you agree to the{' '}
                <a
                  target="_blank"
                  rel="noreferrer"
                  href="/doc/sellscreen/en/terms.pdf"
                  className="text-primary-600"
                >
                  Terms of Service
                </a>{' '}
                and the{' '}
                <a
                  target="_blank"
                  rel="noreferrer"
                  href="/doc/sellscreen/en/privacy_policy.pdf"
                  className="text-primary-600"
                >
                  Privacy Policy
                </a>
              </>
            )}
          </p>
        </div>
      </form>
    </>
  )
}

export default UserInfoForm
